// @ts-nocheck
import { useEffect, useState } from 'react';
import { Send, X, Loader, Info } from 'lucide-react';
import { Button, InfoBanner } from '../../components/WorkflowFormPrimitives';
import { AnalysisDocumentRow } from '../../../../services/AnalysisApiClient';
import { sendDocumentForSignature } from '../../../../services/ApprovalEnvelopeApiClient';

interface Props {
  document: AnalysisDocumentRow | null;
  resend?: boolean;
  onClose: () => void;
  onSent?: (envelope: { id: string; status: string; provider: string }) => void;
  onFailed?: () => void;
}

const PROVIDER_OPTIONS = [
  { value: 'DocuSign', label: 'DocuSign (production e-sign)' },
  { value: 'Internal', label: 'Internal (record manually after delivery)' },
];

function providerOptionsForDocument(doc: AnalysisDocumentRow) {
  const isWaiver = doc.kind === 'Waiver Agreement';
  return isWaiver ? PROVIDER_OPTIONS.filter((o) => o.value === 'DocuSign') : PROVIDER_OPTIONS;
}

const DEFAULT_BLURB =
  'Please review and execute the attached document in connection with the Section 280G ' +
  'shareholder approval process. If you have any questions, contact your company representative ' +
  'before signing.';

export function SendForSignatureDialog({ document: doc, resend = false, onClose, onSent, onFailed }: Props) {
  const [provider, setProvider] = useState<'DocuSign' | 'Internal'>('DocuSign');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState(DEFAULT_BLURB);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!doc) return;
    setSubject(`Action Required: ${doc.title}`);
    setError(null);
    setBusy(false);
    if (doc.kind === 'Waiver Agreement') {
      setProvider('DocuSign');
    }
  }, [doc?.id, doc?.kind]);

  if (!doc) return null;

  const providerOptions = providerOptionsForDocument(doc);
  const dialogTitle = resend ? 'Resend for Signature' : 'Send for Signature';
  const submitLabel = resend ? 'Resend for Signature' : 'Send for Signature';
  const busyLabel = resend ? 'Resending…' : 'Sending…';

  const handleSend = async () => {
    setBusy(true);
    setError(null);
    try {
      const envelope = await sendDocumentForSignature(doc.id, {
        provider,
        emailSubject: subject.trim() || doc.title,
        emailMessage: message.trim(),
      });
      onSent?.(envelope);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send envelope.');
      onFailed?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg border border-gray-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">{dialogTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-50"
            aria-label="Close"
          >
            <X size={16} aria-hidden />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 text-xs">
          <div className="bg-gray-50 border border-gray-200 rounded p-3">
            <div className="text-[11px] text-gray-500 uppercase tracking-wide mb-0.5">Document</div>
            <div className="font-medium text-gray-900">{doc.title}</div>
            <div className="text-gray-500 mt-0.5">
              {doc.kind} · {doc.format} · v{doc.version}
            </div>
          </div>

          {resend ? (
            <InfoBanner variant="info" showIcon>
              Any in-progress envelope for this document will be voided before the new one is sent.
            </InfoBanner>
          ) : null}

          {error ? (
            <InfoBanner variant="danger" showIcon>
              {error}
            </InfoBanner>
          ) : null}

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">Provider</label>
            <div className="grid grid-cols-1 gap-2">
              {providerOptions.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-2 p-2 border rounded cursor-pointer transition ${
                    provider === opt.value
                      ? 'border-emerald-400 bg-emerald-50/40'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="provider"
                    className="mt-0.5"
                    checked={provider === opt.value}
                    onChange={() => setProvider(opt.value as 'DocuSign' | 'Internal')}
                    disabled={busy}
                  />
                  <span className="text-gray-800">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">Email subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={busy}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">Email message</label>
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={busy}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div className="flex items-start gap-2 text-[11px] text-gray-500">
            <Info size={14} className="text-gray-400 mt-0.5 flex-shrink-0" aria-hidden />
            <p>
              Recipients are resolved automatically: <strong>Waiver Agreements</strong> route to the specific DQI;{' '}
              <strong>Disclosure Statements</strong> and <strong>Shareholder Consents</strong> route to every
              non-excluded row in this analysis's cap table.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/40">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSend} disabled={busy || !subject.trim()}>
            {busy ? (
              <>
                <Loader size={14} className="mr-1.5 animate-spin" aria-hidden /> {busyLabel}
              </>
            ) : (
              <>
                <Send size={14} className="mr-1.5" aria-hidden /> {submitLabel}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
